import React, { useState, useEffect } from 'react';
import { X, Bell } from 'lucide-react';
import { Card, Button } from '../design-system';
import NotificationManager from './NotificationManager';
import usePWAStatus from '../../hooks/usePWAStatus';

const NOTIFICATION_PREFERENCE_KEY = 'theraengage_notification_preference';
const NOTIFICATION_PREFERENCE_VALUES = {
  ALLOWED: 'allowed',
  DENIED: 'denied',
  ASK_LATER: 'ask_later'
};

/**
 * NotificationPromptModal - Shows a modal to prompt users for notification permission
 * Only shows in PWA mode and respects user preferences stored in localStorage
 */
const NotificationPromptModal = ({ userId, onSubscriptionChange }) => {
  const { isPWA, isLoading: isPWALoading } = usePWAStatus();
  const [showModal, setShowModal] = useState(false);
  const [preference, setPreference] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showNotificationManager, setShowNotificationManager] = useState(false);

  // Load preference from localStorage on mount and check browser permission
  useEffect(() => {
    const storedPreference = localStorage.getItem(NOTIFICATION_PREFERENCE_KEY);
    setPreference(storedPreference);
    
    // Also check browser's actual notification permission status
    // If browser permission is already granted or denied, respect that
    if ('Notification' in window) {
      const browserPermission = Notification.permission;
      if (browserPermission === 'granted' && !storedPreference) {
        // Browser permission granted but no preference stored - sync it
        localStorage.setItem(NOTIFICATION_PREFERENCE_KEY, NOTIFICATION_PREFERENCE_VALUES.ALLOWED);
        setPreference(NOTIFICATION_PREFERENCE_VALUES.ALLOWED);
      } else if (browserPermission === 'denied' && !storedPreference) {
        // Browser permission denied but no preference stored - sync it
        localStorage.setItem(NOTIFICATION_PREFERENCE_KEY, NOTIFICATION_PREFERENCE_VALUES.DENIED);
        setPreference(NOTIFICATION_PREFERENCE_VALUES.DENIED);
      }
    }
    
    setIsLoading(false);
  }, []);

  // Show modal if:
  // 1. PWA is detected (not in browser)
  // 2. User hasn't made a choice yet OR chose "ask me later"
  // 3. Browser permission is not already granted or denied
  // 4. PWA detection has finished loading
  useEffect(() => {
    if (!isPWALoading && isPWA && !isLoading) {
      // Check browser permission status
      const browserPermission = 'Notification' in window ? Notification.permission : null;
      
      // Don't show if browser permission is already granted or denied
      if (browserPermission === 'granted' || browserPermission === 'denied') {
        setShowModal(false);
        return;
      }
      
      // Only show if no preference stored OR preference is "ask later"
      if (!preference || preference === NOTIFICATION_PREFERENCE_VALUES.ASK_LATER) {
        // Small delay to ensure page is fully loaded
        const timer = setTimeout(() => {
          setShowModal(true);
        }, 1000);
        return () => clearTimeout(timer);
      } else {
        // Preference is set to ALLOWED or DENIED, don't show modal
        setShowModal(false);
      }
    }
  }, [isPWA, isPWALoading, preference, isLoading]);

  const handleAllow = async () => {
    try {
      // Request notification permission directly
      if ('Notification' in window && 'serviceWorker' in navigator) {
        const permission = await Notification.requestPermission();
        
        if (permission === 'granted') {
          // Store preference as "allowed" first
          localStorage.setItem(NOTIFICATION_PREFERENCE_KEY, NOTIFICATION_PREFERENCE_VALUES.ALLOWED);
          setPreference(NOTIFICATION_PREFERENCE_VALUES.ALLOWED);
          // Show NotificationManager to complete subscription setup
          setShowNotificationManager(true);
        } else if (permission === 'denied') {
          // User denied, treat as "Don't Ask Again"
          handleDeny();
        } else {
          // User dismissed, treat as "Ask Me Later"
          handleAskLater();
        }
      } else {
        // Not supported, show error
        setShowNotificationManager(true);
      }
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      setShowNotificationManager(true);
    }
  };

  const handleAskLater = () => {
    localStorage.setItem(NOTIFICATION_PREFERENCE_KEY, NOTIFICATION_PREFERENCE_VALUES.ASK_LATER);
    setPreference(NOTIFICATION_PREFERENCE_VALUES.ASK_LATER);
    setShowModal(false);
  };

  const handleDeny = () => {
    localStorage.setItem(NOTIFICATION_PREFERENCE_KEY, NOTIFICATION_PREFERENCE_VALUES.DENIED);
    setPreference(NOTIFICATION_PREFERENCE_VALUES.DENIED);
    setShowModal(false);
  };

  const handleClose = () => {
    // If user closes without choosing, treat as "ask me later"
    handleAskLater();
  };

  const handleSubscriptionSuccess = (subscription) => {
    // When subscription is successful, close the modal
    if (subscription) {
      setShowModal(false);
    }
    if (onSubscriptionChange) {
      onSubscriptionChange(subscription);
    }
  };

  // Don't show modal if:
  // - Still loading PWA status or preference
  // - Not in PWA mode (browser)
  // - User has denied or allowed
  // - Modal is explicitly closed
  // - Browser permission is already granted or denied
  const browserPermission = 'Notification' in window ? Notification.permission : null;
  if (
    isPWALoading || 
    isLoading ||
    !isPWA || 
    preference === NOTIFICATION_PREFERENCE_VALUES.DENIED || 
    preference === NOTIFICATION_PREFERENCE_VALUES.ALLOWED ||
    browserPermission === 'granted' ||
    browserPermission === 'denied' ||
    !showModal
  ) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <Card variant="elevated" padding="large" className="max-w-md w-full">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-indigo-100 rounded-full">
              <Bell className="h-6 w-6 text-indigo-600" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900">Enable Notifications</h3>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors p-1"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="mb-6">
          <p className="text-gray-600 mb-4">
            Stay updated with important reminders, appointment notifications, and task updates. 
            You can change this setting anytime.
          </p>

          {showNotificationManager && (
            <NotificationManager 
              userId={userId} 
              onSubscriptionChange={handleSubscriptionSuccess}
            />
          )}
        </div>

        {!showNotificationManager && (
          <div className="flex flex-col sm:flex-row gap-3 justify-end">
            <Button
              variant="primary"
              size="medium"
              onClick={handleAllow}
              className="w-full sm:w-auto"
            >
              Allow
            </Button>
            <Button
              variant="outline"
              size="medium"
              onClick={handleAskLater}
              className="w-full sm:w-auto"
            >
              Ask Me Later
            </Button>
            <Button
              variant="outline"
              size="medium"
              onClick={handleDeny}
              className="w-full sm:w-auto text-gray-600 hover:text-gray-800"
            >
              Don't Ask Again
            </Button>
          </div>
        )}
      </Card>
    </div>
  );
};

export default NotificationPromptModal;
export { NOTIFICATION_PREFERENCE_KEY, NOTIFICATION_PREFERENCE_VALUES };

